import { Injectable, OnInit } from '@angular/core';

import * as day from 'dayjs';
import { IHallPass, IMingaFeatureToggleKeys, MingaPermission, StudentSection } from 'libs/domain';
import { flexPeriodToStudentSection, flexToStudentSection } from 'libs/shared';
import { mingaSettingTypes } from 'libs/util';
import { BehaviorSubject, combineLatest, from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppConfigService } from '@app/src/app/minimal/services/AppConfig';
import { PermissionsService } from '@app/src/app/permissions';
import { HallPassService } from '@app/src/app/services/HallPass';
import { FlexTimePeriodService, FlexTimePermissionsService } from '@app/src/app/shared/services/flex-time';
import { MingaSettingsService } from '@app/src/app/store/Minga/services';

import { BellSchedulePermissionsService } from '@modules/minga-manager/components/mm-bell-schedule/services/bell-schedule-permissions.service';
import { StudentToolsFlexTimeService } from '@modules/tools/student-tools/services';

import { SystemAlertSnackBarService } from '@shared/components/system-alert-snackbar';
import { StudentScheduleService } from '@shared/services/student-schedule';
import { AuthInfoService } from '@app/src/app/minimal/services/AuthInfo';

@Injectable()
export abstract class StudentDashboardAbstract implements OnInit {
  protected _activeHallPass = new BehaviorSubject<IHallPass>(null);
  public readonly activeHallPass$ = this._activeHallPass.asObservable();

  protected _loadingScheduleSubject = new BehaviorSubject<boolean>(true);
  public loadingSchedule$ = this._loadingScheduleSubject.asObservable();

  protected _scheduleItemsSubject = new BehaviorSubject<StudentSection[]>([]);
  public scheduleItems$ = this._scheduleItemsSubject.asObservable().pipe(
    map(items =>
      items.sort((a, b) => {
        return a.startTime < b.startTime ? -1 : 1;
      }),
    ),
  );

  public isBellScheduleEnabledForCurrentUser$: Observable<boolean> = from(
    this._bellSchedulePermissions.isBellScheduleEnabledForCurrentUser(),
  );

  public isScheduleEnabled$: Observable<boolean> = combineLatest([
    this._mingaSettings.isFlexTimeModuleEnabled(),
    this._permissions.observePermission(MingaPermission.STUDENT_SCHEDULE_VIEW),
    this._mingaSettings.getSettingValueObs(mingaSettingTypes.STUDENT_SCHEDULE_ENABLE),
  ]).pipe(
    map(
      ([flexEnabled, canViewStudentSchedule, studentScheduleEnabled]) =>
        (flexEnabled || (canViewStudentSchedule && this._appConfig.getValue('studentScheduleEnabled'))) &&
        studentScheduleEnabled,
    ),
  );

  constructor(
    private _hallPassService: HallPassService,
    private _studentScheduleService: StudentScheduleService,
    private _studentFlexTime: StudentToolsFlexTimeService,
    private _mingaSettings: MingaSettingsService,
    private _snackBarService: SystemAlertSnackBarService,
    private _appConfig: AppConfigService,
    private _permissions: PermissionsService,
    private _flexPermissions: FlexTimePermissionsService,
    private _bellSchedulePermissions: BellSchedulePermissionsService,
    private _flexPeriod: FlexTimePeriodService,
    private _authInfoService: AuthInfoService,
  ) {}

  ngOnInit(): void {
    this.onInit();
  }

  protected abstract onInit(): void;

  protected _fetchStudentData(userHash: string, isStudent: boolean): void {
    this._fetchActiveHallPass(userHash);

    if (this._appConfig.getValue('studentScheduleEnabled')) {
      this._fetchMySchedule(userHash, isStudent);
    }
  }

  private async _fetchActiveHallPass(userHash: string): Promise<void> {
    const hallPasses = await this._hallPassService.getHallPassesForRecipient(userHash);
    const activePass = hallPasses.find(pass => pass.endDate > new Date());
    if (activePass) {
      this._activeHallPass.next(activePass);
    }
  }

  private async _fetchMySchedule(userHash: string, isStudent: boolean): Promise<void> {
    try {
      this._loadingScheduleSubject.next(true);
      const start = day();
      const end = day();
      await Promise.all([
        this._fetchStudentSchedule(userHash, start, end),
        this._fetchFlexTimeSchedule(userHash, start, end, isStudent),
      ]);
    } catch (e) {
      this._snackBarService.error('There was a problem fetching schedule');
    } finally {
      this._loadingScheduleSubject.next(false);
    }
  }

  private async _fetchStudentSchedule(userHash: string, start: day.Dayjs, end: day.Dayjs): Promise<void> {
    const isStudentScheduleEnabled = await this._mingaSettings.getSettingValue(
      mingaSettingTypes.STUDENT_SCHEDULE_ENABLE,
    );
    if (!isStudentScheduleEnabled) return;

    const studentSchedule = await this._studentScheduleService.getStudentSchedule(userHash, start, end);

    const existing = this._scheduleItemsSubject.value;
    this._scheduleItemsSubject.next([...existing, ...studentSchedule]);
  }

  private async _fetchFlexTimeSchedule(
    userHash: string,
    start: day.Dayjs,
    end: day.Dayjs,
    isStudent: boolean,
  ): Promise<void> {
    const isFlexEnabled = await this._mingaSettings.getModuleEnabled(IMingaFeatureToggleKeys.FLEX_TIME_ENABLED);
    if (!isFlexEnabled) return;

    if (isStudent) {
      // students - activities they are registered to
      const registrations = await this._studentFlexTime.fetchMyActivityRegistrations(start, end, userHash);
      const studentCards = registrations.map(r => flexToStudentSection(r));
      this._scheduleItemsSubject.next([...this._scheduleItemsSubject.value, ...studentCards]);
    } else if (this._flexPermissions.isFlexTimeSelfManagerOrHigher()) {
      // teachers (must have flextime permission) - activities they are teaching

      const authUserHash = this._authInfoService.authPersonHash;
      const isRequestingOwnActivities = userHash === authUserHash;

      const periods = await this._flexPeriod.fetchAll(start, end, isRequestingOwnActivities, false, isRequestingOwnActivities ? null : userHash);
      const classes = periods.filter(p => Array.isArray(p.activityInstances) && p.activityInstances.length > 0 )
      const teacherCards = classes.map(p => flexPeriodToStudentSection(p));
      this._scheduleItemsSubject.next([...this._scheduleItemsSubject.value, ...teacherCards]);
    }
  }
}
